import React, { useMemo, useState } from 'react';
import type { DateRange, MonthSeries, RangeMetrics, SeriesSet } from '../../types/dashboard';
import { Panel } from '../ui/Panel';
import { SortableTh } from '../ui/SortableTh';
import { ExportButton } from '../ui/TableControls';
import { EmptyState } from '../ui/EmptyState';
import { useSort } from '../../hooks/useSort';
import { monthLabel, rangeMonths } from '../../utils/calendar';
import { perUnitRange, rangeMetrics } from '../../utils/metrics';
import { change, formatChange, formatValue, isCountMetric, metricMeta, monthlyBasis, monthValue, rangeValue, type TrendMetric } from '../../utils/metricConfig';
import { fmtCurrency, fmtInt, fmtRatio } from '../../utils/format';
import { downloadCsv } from '../../utils/csv';
interface Stats {
  members: number;
  converted: number;
  registrations: number;
  revenue: number;
  digital: number | null;
  local: number | null;
  unknown: number | null;
}
interface ExtraCol {
  key: string;
  label: string;
  get: (s: Stats) => number | null;
  fmt: (v: number) => string;
}
const div = (a: number, b: number) => b ? a / b : null;
function extrasFor(metric: TrendMetric): ExtraCol[] {
  const f = (v: number) => formatValue(metric, v);
  switch (metric) {
    case 'members':
      return [{
        key: 'digital',
        label: 'Digital',
        get: s => s.digital,
        fmt: f
      }, {
        key: 'local',
        label: 'Local / Other',
        get: s => s.local,
        fmt: f
      }, {
        key: 'unknown',
        label: 'Unknown',
        get: s => s.unknown,
        fmt: f
      }];
    case 'registrations':
      return [{
        key: 'converted',
        label: 'Unique members registered',
        get: s => s.converted,
        fmt: fmtInt
      }, {
        key: 'rpm',
        label: 'Regs / member',
        get: s => div(s.registrations, s.converted),
        fmt: v => fmtRatio(v)
      }];
    case 'revenue':
      return [{
        key: 'registrations',
        label: 'Registrations',
        get: s => s.registrations,
        fmt: fmtInt
      }, {
        key: 'rpr',
        label: 'Revenue / registration',
        get: s => div(s.revenue, s.registrations),
        fmt: fmtCurrency
      }];
    case 'conversion':
      return [{
        key: 'members',
        label: 'Members',
        get: s => s.members,
        fmt: fmtInt
      }, {
        key: 'converted',
        label: 'Converted members',
        get: s => s.converted,
        fmt: fmtInt
      }];
    case 'regsPerMember':
      return [{
        key: 'registrations',
        label: 'Registrations',
        get: s => s.registrations,
        fmt: fmtInt
      }, {
        key: 'converted',
        label: 'Contributing members',
        get: s => s.converted,
        fmt: fmtInt
      }];
  }
}
interface Row {
  m: number;
  label: string;
  values: Record<string, number | null>;
}
interface MonthlyTableProps {
  metric: TrendMetric;
  series: SeriesSet;
  network: SeriesSet;
  range: DateRange;
  scopeUnits: (m: number) => number;
  networkUnits: (m: number) => number;
  onMonthClick: (m: number) => void;
}
type RowFilter = 'all' | 'yoyDown' | 'yoyUp';
export function MonthlyTable({
  metric,
  series,
  network,
  range,
  scopeUnits,
  networkUnits,
  onMonthClick
}: MonthlyTableProps) {
  const [rowFilter, setRowFilter] = useState<RowFilter>('all');
  const count = isCountMetric(metric);
  const meta = metricMeta(metric);
  const extras = useMemo(() => extrasFor(metric), [metric]);
  const fmt = (v: number) => formatValue(metric, v);
  const monthStats = (s: MonthSeries, d: MonthSeries, l: MonthSeries, u: MonthSeries, m: number): Stats => ({
    members: s.members[m],
    converted: s.conv[0][m],
    registrations: s.regs[0][m],
    revenue: s.rev[0][m],
    digital: monthValue(metric, d, m),
    local: monthValue(metric, l, m),
    unknown: monthValue(metric, u, m)
  });
  const periodStats = (r: RangeMetrics): Stats => ({
    members: r.members,
    converted: r.registeringMembers,
    registrations: r.registrations,
    revenue: r.revenue,
    digital: rangeValue(metric, rangeMetrics(series.Digital, range.start, range.end)),
    local: rangeValue(metric, rangeMetrics(series.Local, range.start, range.end)),
    unknown: rangeValue(metric, rangeMetrics(series.Unknown, range.start, range.end))
  });
  const rows = useMemo<Row[]>(() => rangeMonths(range).map(m => {
    const total = monthValue(metric, series.all, m);
    const active = scopeUnits(m);
    const st = monthStats(series.all, series.Digital, series.Local, series.Unknown, m);
    const values: Record<string, number | null> = {
      m,
      active,
      total,
      avg: count ? active && total !== null ? total / active : null : monthValue(metric, network.all, m),
      mom: change(metric, total, monthValue(metric, series.all, m - 1)),
      yoy: change(metric, total, monthValue(metric, series.all, m - 12))
    };
    extras.forEach(c => values[c.key] = c.get(st));
    return {
      m,
      label: monthLabel(m),
      values
    };
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [metric, series, network, range, extras]);
  const visible = useMemo(() => rows.filter(r => rowFilter === 'all' || (rowFilter === 'yoyDown' ? (r.values.yoy ?? 0) < 0 : (r.values.yoy ?? 0) > 0)), [rows, rowFilter]);
  const {
    sorted,
    sort,
    onSort
  } = useSort<Row, string>(visible, {
    key: 'm',
    dir: 'desc'
  }, (r, k) => r.values[k] ?? null);
  const period = rangeMetrics(series.all, range.start, range.end);
  const periodTotal = rangeValue(metric, period);
  const activeMonthCount = rangeMonths(range).filter(m => scopeUnits(m) > 0).length;
  // Counts: mean of the monthly (total ÷ active territories) values — never a 13-month sum.
  const periodAvg = count ? activeMonthCount ? rangeValue(metric, perUnitRange(series.all, range.start, range.end, scopeUnits)) / activeMonthCount : 0 : rangeValue(metric, rangeMetrics(network.all, range.start, range.end));
  const pStats = periodStats(period);
  const totalLabel = metric === 'members' ? 'New members' : count ? `Total ${meta.label.toLowerCase()}` : meta.label;
  const avgLabel = count ? 'Avg / active territory' : 'Network';
  const changeCell = (v: number | null) => v === null ? <span className="text-ink-subtle" data-id="element-325" data-mp="3t3robjol9jzq">—</span> : <span className={v < 0 ? 'text-negative' : 'text-positive'} data-id="element-326" data-mp="3t2oxfbr9nhjt">{formatChange(metric, v)}</span>;
  const exportCsv = () => {
    const pct = (v: number | null) => v === null ? '' : (v * 100).toFixed(2);
    downloadCsv(`i9-monthly-${metric}-${range.label.replace(/[^a-z0-9]+/gi, '-')}.csv`, ['Month', 'Active Territories', totalLabel, avgLabel, metric === 'conversion' ? 'MoM vs prior month (pp)' : 'MoM vs prior month %', metric === 'conversion' ? 'YoY vs same month last year (pp)' : 'YoY vs same month last year %', ...extras.map(e => e.label)], sorted.map(r => [r.label, r.values.active ?? '', r.values.total === null ? '' : Number(r.values.total.toFixed(4)), r.values.avg === null ? '' : Number(r.values.avg.toFixed(4)), pct(r.values.mom), pct(r.values.yoy), ...extras.map(e => r.values[e.key] === null ? '' : Number((r.values[e.key] as number).toFixed(4)))]));
  };
  return <Panel title={`Monthly ${meta.label.toLowerCase()}`} definitionId={meta.definitionId} subtitle={`Each row: ${monthlyBasis(metric)}. Click a row for month detail.`} actions={<>
          <label className="flex items-center gap-1.5 text-xs text-ink-muted" data-id="element-328" data-mp="2075vo4pkr6pl">
            Show
            <select value={rowFilter} onChange={e => setRowFilter(e.target.value as RowFilter)} className="h-8 rounded-md border border-line bg-white px-2 text-xs text-ink focus:border-navy/50 focus:outline-none" data-id="element-329" data-mp="208z15gogbkw0">
              <option value="all" data-id="element-330" data-mp="1lk2yjz55z02s">All months</option>
              <option value="yoyDown" data-id="element-331" data-mp="1hh68xoh7i3pc">YoY decline only</option>
              <option value="yoyUp" data-id="element-332" data-mp="1hir8jsidocu2">YoY growth only</option>
            </select>
          </label>
          <ExportButton onClick={exportCsv} data-id="element-333" data-mp="1cjo9qpszr2uj" />
        </>} bodyClassName="p-0" data-id="element-327" data-mp="2e4uhberguhvp">
      {sorted.length === 0 ? <EmptyState title="No months match this filter" description="Switch the row filter back to All months." data-id="element-334" data-mp="35d43mpwv1jpf" /> : <div className="max-h-[420px] overflow-auto" data-id="element-335" data-mp="2rcjlzx2oltxi">
          <table className="w-full min-w-[920px] border-collapse text-[12.5px]" data-id="element-336" data-mp="2rfxrvfak0l9p">
            <thead className="sticky top-0 z-10 bg-navy-50 text-navy" data-id="element-337" data-mp="2vh9hhktfh6hw">
              <tr data-id="element-338" data-mp="2hn034reyy02n">
                <SortableTh label="Month" sortKey="m" sort={sort} onSort={onSort} align="left" className="pl-5" data-id="element-339" data-mp="2hmgw45quww3e" />
                <SortableTh label="Active territories" sortKey="active" sort={sort} onSort={onSort} data-id="element-340" data-mp="2mgbbiwzfiowo" />
                <SortableTh label={totalLabel} sortKey="total" sort={sort} onSort={onSort} data-id="element-341" data-mp="27nr9y0rxgphc" />
                <SortableTh label={avgLabel} sortKey="avg" sort={sort} onSort={onSort} data-id="element-342" data-mp="2cmm604ewoaje" />
                <SortableTh label="MoM vs prior mo" sortKey="mom" sort={sort} onSort={onSort} data-id="element-343" data-mp="1yppctwbbegzc" />
                <SortableTh label="YoY vs same mo LY" sortKey="yoy" sort={sort} onSort={onSort} data-id="element-344" data-mp="23n7d6yb9nnvq" />
                {extras.map((e, i) => <SortableTh key={e.key} label={e.label} sortKey={e.key} sort={sort} onSort={onSort} className={i === extras.length - 1 ? 'pr-5' : ''} data-id="element-345" data-mp="1f3dth26qbpb6" data-mpi={i} />)}
              </tr>
            </thead>
            <tbody className="tabular-nums" data-id="element-346" data-mp="1p0s7kv56k20b">
              {sorted.map((r, i) => <tr key={r.m} tabIndex={0} onClick={() => onMonthClick(r.m)} onKeyDown={e => e.key === 'Enter' && onMonthClick(r.m)} className={`cursor-pointer border-b border-line transition-colors duration-100 hover:bg-navy-50 focus-visible:bg-navy-50 focus-visible:outline-none ${i % 2 ? 'bg-canvas/50' : ''}`} data-id="element-347" data-mp="230t8galg39fs" data-mpi={i}>
                  <td className="whitespace-nowrap py-2 pl-5 pr-3 font-medium text-navy" data-id="element-348" data-mp="2lqsqwems5qa8">{r.label}</td>
                  <td className="px-3 py-2 text-right" data-id="element-349" data-mp="2gtiwslog4qg2">{r.values.active}</td>
                  <td className="px-3 py-2 text-right font-semibold" data-id="element-350" data-mp="2bhktuka606kb">{r.values.total === null ? '—' : fmt(r.values.total)}</td>
                  <td className="px-3 py-2 text-right text-ink-muted" data-id="element-351" data-mp="26pb537ksdcjm">{r.values.avg === null ? '—' : fmt(r.values.avg)}</td>
                  <td className="px-3 py-2 text-right" data-id="element-352" data-mp="21rj65tlq4q5x">{changeCell(r.values.mom)}</td>
                  <td className="px-3 py-2 text-right" data-id="element-353" data-mp="1wtq0d1oc1y66">{changeCell(r.values.yoy)}</td>
                  {extras.map((e, j) => {
              const v = r.values[e.key];
              return <td key={e.key} className={`py-2 text-right ${j === extras.length - 1 ? 'pl-3 pr-5' : 'px-3'}`} data-id="element-354" data-mp="2phdk6ehs4krr" data-mpi={j}>
                        {v === null ? '—' : e.fmt(v)}
                      </td>;
            })}
                </tr>)}
            </tbody>
            <tfoot className="sticky bottom-0 bg-white tabular-nums" data-id="element-355" data-mp="1945fgd8mcqu9">
              <tr className="border-t-2 border-navy/40 font-semibold text-navy" data-id="element-356" data-mp="1437whyfsbsrp">
                <td className="py-2.5 pl-5 pr-3" data-id="element-357" data-mp="3frcuhqwapw71">Selected period</td>
                <td className="px-3 py-2.5 text-right text-ink-subtle" data-id="element-358" data-mp="3fp020jsww3fq">—</td>
                <td className="px-3 py-2.5 text-right" data-id="element-359" data-mp="3kqh4ukuyhzyy">{fmt(periodTotal)}</td>
                <td className="px-3 py-2.5 text-right" data-id="element-360" data-mp="3pnzao2dofz2q">
                  {fmt(periodAvg)}
                  {count && <span className="ml-1 text-[11px] font-normal text-ink-subtle" data-id="element-361" data-mp="3ugird87nvabr">monthly mean</span>}
                </td>
                <td className="px-3 py-2.5 text-right text-ink-subtle" data-id="element-362" data-mp="3968mor4qotq">—</td>
                <td className="px-3 py-2.5 text-right text-ink-subtle" data-id="element-363" data-mp="79q4zq3vpoet">—</td>
                {extras.map((e, j) => {
              const v = e.get(pStats);
              return <td key={e.key} className={`py-2.5 text-right ${j === extras.length - 1 ? 'pl-3 pr-5' : 'px-3'}`} data-id="element-364" data-mp="h85vpryzuvns" data-mpi={j}>
                      {v === null ? '—' : e.fmt(v)}
                    </td>;
            })}
              </tr>
            </tfoot>
          </table>
        </div>}
      <p className="border-t border-line px-5 py-2.5 text-[11.5px] text-ink-subtle" data-id="element-365" data-mp="2b0ns0v60yrfd">
        {metric === 'members' ? 'Selected-period New members = sum of monthly rows. Avg / active territory = that month’s new members ÷ active territories that month; the period value is the mean of the monthly averages. ' : metric === 'conversion' ? 'Monthly rows show same-month acquisition and conversion. The selected-period row lets members acquired earlier in the period convert in a later month of the period, so it is usually higher than the individual monthly rates. Network column shows the network rate for the same month. ' : `The period row also includes members who registered in a later month within the range, so it can differ from the sum of monthly rows. ${count ? 'Avg / active territory = monthly total ÷ active territories that month; the period value is a monthly mean.' : 'Network column shows the network rate for the same month.'} `}
        MoM compares each month to the prior month; YoY to the same month one year earlier. Oct ’26 is month-to-date and excluded.
      </p>
    </Panel>;
}